'use client'

import { useState } from 'react'
import { ChevronDown } from 'lucide-react'

export type SpecsTableProps = {
  specs: Array<{ label: string; value: string }>
  expandable?: boolean
  /** Number of rows visible when collapsed (default 6) */
  initialRows?: number
}

/**
 * Zebra-striped label/value table for product specifications. Purely
 * presentational — callers normalize their data source into
 * `{ label, value }` pairs (the storefront PDP does this from
 * `products.attributes`). Renders nothing when `specs` is empty, so callers
 * can pass it unconditionally.
 */
export function SpecsTable({ specs, expandable = false, initialRows = 6 }: SpecsTableProps) {
  const [expanded, setExpanded] = useState(false)
  if (specs.length === 0) return null

  const visibleSpecs = expandable && !expanded ? specs.slice(0, initialRows) : specs
  const hasMore = expandable && specs.length > initialRows

  return (
    <div data-component="SpecsTable">
      <table className="w-full text-sm" style={{ fontFamily: 'var(--font-body)' }}>
        <tbody>
          {visibleSpecs.map((spec, i) => (
            <tr
              key={`${spec.label}-${i}`}
              className={i % 2 === 0 ? 'bg-[var(--color-bg-secondary)]' : 'bg-[var(--color-bg-primary)]'}
            >
              <td className="py-2.5 px-3 font-medium text-[var(--color-text-primary)] w-1/3">
                {spec.label}
              </td>
              <td className="py-2.5 px-3 text-[var(--color-text-secondary)]">
                {spec.value}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {hasMore && (
        <button
          onClick={() => setExpanded(!expanded)}
          className="mt-2 flex items-center gap-1 text-sm text-[var(--color-link)] hover:text-[var(--color-link-hover)] transition-colors"
        >
          {expanded ? 'Show less' : `Show all ${specs.length} specs`}
          <ChevronDown
            size={16}
            className={`transition-transform ${expanded ? 'rotate-180' : ''}`}
          />
        </button>
      )}
    </div>
  )
}
